import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { PrimaryButton } from "@/components/ui/Buttons";
import {
  useResendOtpMutation,
  useVerifyEmailMutation,
} from "@/redux/feature/auth/authApi";
import { VerifyToken } from "@/utils/verifyToken";
import { setUser } from "@/redux/feature/auth/authSlice";
import { useAppDispatch } from "@/redux/hooks/hooks";
import { useNavigate, useNavigation } from "react-router-dom";

interface VerifyAccountProps {
  email: string;
  onVerificationSuccess: () => void;
}

const VerifyAccount: React.FC<VerifyAccountProps> = ({
  email,
  onVerificationSuccess,
}) => {
  // State for OTP input
  const [values, setValues] = useState<string[]>(new Array(6).fill(""));
  const [error, setError] = useState("");
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  // State for OTP resend functionality
  const [resendOtp] = useResendOtpMutation();
  const [isLoading, setLoading] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);
  const [lastOtpSent, setLastOtpSent] = useState<number | null>(null);
  const [verifyEmail] = useVerifyEmailMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Effect to manage OTP resend timer
  useEffect(() => {
    // Check if OTP was previously sent
    const storedOtpTime = localStorage.getItem("otpSentTime");
    if (storedOtpTime) {
      const otpTime = parseInt(storedOtpTime);
      setLastOtpSent(otpTime);
    }
  }, []);

  // Effect to manage countdown
  useEffect(() => {
    if (lastOtpSent !== null) {
      const interval = setInterval(() => {
        const timeElapsed = Math.floor((Date.now() - lastOtpSent) / 1000);
        const timeRemaining = 120 - timeElapsed; // 2 minutes

        if (timeRemaining <= 0) {
          clearInterval(interval);
          setTimeLeft(0);
          setIsWaiting(false); // Enable Resend button
        } else {
          setTimeLeft(timeRemaining);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [lastOtpSent]);

  // Input handling methods
  const handleChange = (value: string, index: number) => {
    const newValues = [...values];
    newValues[index] = value;

    if (value.length === 1 && index < 5) {
      inputs.current[index + 1]?.focus();
    }

    setValues(newValues);
    setError("");
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace" && values[index] === "" && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData("text").slice(0, 6);
    const pasteValues = paste.split("");
    const newValues = [...values];

    pasteValues.forEach((value, i) => {
      newValues[i] = value;
      if (i < 5) inputs.current[i + 1]?.focus();
    });

    setValues(newValues);
    setError("");
    e.preventDefault();
  };

  // OTP Verification Handler
  const handleSubmit = async () => {
    if (values.some((value) => value === "")) {
      setError("Please fill up all fields.");
      return;
    }
    setLoading(true);
    const code = values.join("");

    const payload = {
      email,
      otp: code,
    };

    try {
      const res = await verifyEmail(payload).unwrap();
      if (res.data.success) {
        const user = VerifyToken(res.data.accessToken);
        dispatch(setUser({ user: user, token: res.data.accessToken }));

        toast.success("Email Verified");

        // check if the admin already uploaded the creator or not if yes will skip the form submittion part
        if (res.data.isCreatorApproved) {
          navigate("/dashboard");
        } else {
          onVerificationSuccess();
        }
      } else {
        toast.error(res?.data?.message);
      }
    } catch (err) {
      const error = err as { data?: { errorSource?: { message: string }[] } };
      if (error?.data?.errorSource?.length) {
        error.data.errorSource.forEach((item) => toast.error(item.message));
      } else {
        toast.error("Something Went Wrong");
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP Handler
  const handleRequestForNewOtp = async () => {
    if (isWaiting) {
      toast.error("Please wait 2 minutes before requesting a new OTP.");
      return;
    }

    const payload = { email };
    setLoading(true);

    try {
      // Add more detailed logging
      console.log("Attempting to resend OTP for email:", email);
      const response = await resendOtp(payload).unwrap();

      // Log the response to see what's returned
      console.log("Resend OTP response:", response);

      setLoading(false);
      setIsWaiting(true);
      setLastOtpSent(Date.now());
      localStorage.setItem("otpSentTime", Date.now().toString());

      toast.success("OTP sent to your email!");
    } catch (err) {
      // More detailed error logging
      console.error("Resend OTP error:", err);

      setIsWaiting(false);
      setLoading(false);

      const error = err as { data?: { errorSource?: { message: string }[] } };

      if (error?.data?.errorSource?.length) {
        error.data.errorSource.forEach((item) => {
          console.error("Error detail:", item.message);
          toast.error(item.message);
        });
      } else {
        toast.error("Something Went Wrong");
      }
    }
  };

  return (
    <div className="max-w-[504px] mx-auto">
      <div className="space-y-1 text-center leading-5 mb-8">
        <h1 className="text-[#101828] text-xl font-semibold text-center tracking-[-1%]">
          Confirm your email
        </h1>
        <p className="text-[#475467] text-sm leading-5 tracking-[-1%]">
          Enter the verification code we emailed to
        </p>
        <p className="text-sm text-[#475467] leading-5 tracking-[-1%]">
          {email}{" "}
          <span className="text-[#344054] underline">
            (Use a different email)
          </span>
        </p>
      </div>

      <div className="flex-1">
        <div className="space-y-3 py-[30px]">
          <div>
            <div className="flex gap-2 lg:gap-6 justify-between">
              {values.map((value, index) => (
                <div key={index} className="aspect-square h-13">
                  <input
                    ref={(el) => {
                      inputs.current[index] = el;
                    }}
                    type="text"
                    value={value}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onChange={(e) => handleChange(e.target.value, index)}
                    onPaste={handlePaste}
                    maxLength={1}
                    className="w-full h-full text-center border border-[#D0D5DD] focus:outline-yellow-500 rounded-[8px] text-sm placeholder:text-sm"
                  />
                </div>
              ))}
            </div>
            {error && <p className="text-red-500 mt-2 text-sm">{error}</p>}
          </div>

          <button
            className="text-sm lg:text-base text-[#344054]"
            onClick={handleRequestForNewOtp}
            disabled={isWaiting}
          >
            {isWaiting
              ? `You will be able to resend the code in ${timeLeft} seconds.`
              : isLoading
                ? "Sending"
                : "Resend Code"}
          </button>
        </div>

        <div className="">
          <PrimaryButton
            disabled={isLoading}
            onClick={handleSubmit}
            color="#17457D"
            className="text-base leading-[24px] w-full py-[10px]"
          >
            {isLoading ? (
              <div className="flex space-x-2 justify-center items-center py-2">
                <span className="sr-only">Loading...</span>
                <div className="h-2 w-2 bg-white rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                <div className="h-2 w-2 bg-white rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                <div className="h-2 w-2 bg-white rounded-full animate-bounce"></div>
              </div>
            ) : (
              "Verify"
            )}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default VerifyAccount;
